import type { CallEndReason, CallMedia, IceServersDto, ServerEvent, UserSummaryDto } from '@dijitalapp/shared';
import { api } from './api';
import { uuidv7 } from './outbox';
import type { RealtimeClient } from './realtime';
import { loadCustomBackground, loadSegmenter, presetBackground, saveCustomBackground, type BackgroundChoice, type BackgroundPreset } from './backgrounds';
import { VideoEffectProcessor, type BackgroundLayer, type VideoEffect } from './video-effects';

/**
 * 1:1 WebRTC calls (ADR-015). The browser does all media and DTLS-SRTP encryption; this class only runs
 * the offer/answer exchange over the realtime gateway and keeps the UI state.
 */

export type CallPhase = 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';
export type CallFailure = CallEndReason | 'permission' | 'unsupported' | 'not_allowed';

export interface CallView {
  callId: string;
  conversationId: string;
  peer: Pick<UserSummaryDto, 'id' | 'displayName'>;
  media: CallMedia;
  phase: CallPhase;
  muted: boolean;
  cameraOff: boolean;
  /** Time the media connected (for the call timer). */
  connectedAt: number | null;
  endReason: CallFailure | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** Live effect on my camera picture. */
  effect: VideoEffect;
  /** The camera can blur its own background (some devices/browsers), and whether that is on. */
  deviceBlur: { available: boolean; on: boolean };
  /** Output device for the other side's voice (where the browser lets pages choose). */
  speakerId: string | null;
  /** What is behind me in the picture, and whether the person detection is ready. */
  background: BackgroundChoice;
  backgroundStatus: 'idle' | 'loading' | 'ready' | 'failed';
}

/** Remembered on this device between calls. */
export interface CallSettings {
  micId: string | null;
  camId: string | null;
  speakerId: string | null;
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  effect: VideoEffect;
  deviceBlur: boolean;
  background: BackgroundChoice;
}

const SETTINGS_KEY = 'da.callSettings';
const DEFAULT_SETTINGS: CallSettings = {
  micId: null,
  camId: null,
  speakerId: null,
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  effect: 'none',
  deviceBlur: false,
  background: 'none',
};

export function loadCallSettings(): CallSettings {
  try {
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<CallSettings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveCallSettings(s: CallSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable: settings last for this call only */
  }
}

type BlurCapable = MediaTrackCapabilities & { backgroundBlur?: boolean[] };

export function callsSupported(): boolean {
  return typeof RTCPeerConnection !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

export class CallManager {
  view: CallView | null = null;
  private pc: RTCPeerConnection | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private incomingOffer: string | null = null;
  private listeners = new Set<() => void>();
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private ringer: { stop: () => void } | null = null;
  settings: CallSettings = loadCallSettings();
  /** The camera track as it comes from the device (the sent one may be an effect canvas). */
  private camTrack: MediaStreamTrack | null = null;
  private processor: VideoEffectProcessor | null = null;
  private bitmaps = new Map<string, ImageBitmap>();

  constructor(private readonly rt: RealtimeClient) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private update(patch: Partial<CallView>) {
    if (!this.view) return;
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((l) => l());
  }

  private async connection(): Promise<RTCPeerConnection> {
    const ice = await api<IceServersDto>('GET', '/calls/ice-servers');
    const pc = new RTCPeerConnection({
      iceServers: ice.iceServers,
      // Relay-only hides each side's IP address from the other (media goes through TURN).
      iceTransportPolicy: ice.relayOnly ? 'relay' : 'all',
    });
    pc.onicecandidate = (e) => {
      if (e.candidate && this.view) this.rt.command('call.ice', { callId: this.view.callId, candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.update({ remoteStream: stream });
    };
    pc.onconnectionstatechange = () => {
      if (!this.view || this.pc !== pc) return;
      if (pc.connectionState === 'connected') this.update({ phase: 'active', connectedAt: this.view.connectedAt ?? Date.now() });
      if (pc.connectionState === 'failed') this.hangup('failed');
    };
    this.pc = pc;
    return pc;
  }

  private audioConstraints(): MediaTrackConstraints {
    const s = this.settings;
    return {
      echoCancellation: s.echoCancellation,
      noiseSuppression: s.noiseSuppression,
      autoGainControl: s.autoGainControl,
      ...(s.micId ? { deviceId: { ideal: s.micId } } : {}),
    };
  }

  private videoConstraints(facing?: 'user' | 'environment'): MediaTrackConstraints {
    return {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      ...(facing ? { facingMode: { ideal: facing } } : this.settings.camId ? { deviceId: { ideal: this.settings.camId } } : {}),
    };
  }

  /** Microphone (and camera) with the remembered devices and processing; effects applied to the camera. */
  private async media(video: boolean): Promise<MediaStream> {
    const raw = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints(), video: video ? this.videoConstraints() : false });
    const audio = raw.getAudioTracks();
    const cam = raw.getVideoTracks()[0] ?? null;
    this.camTrack = cam;
    if (!cam) return raw;
    await this.applyDeviceBlur(cam, this.settings.deviceBlur);
    return new MediaStream([...audio, this.videoOut(cam)]);
  }

  /** The track to send for a camera track: itself, or the effect canvas drawing it. */
  private videoOut(cam: MediaStreamTrack): MediaStreamTrack {
    this.processor?.stop();
    this.processor = null;
    if (!this.needsProcessor()) return cam;
    try {
      this.processor = new VideoEffectProcessor(cam, this.settings.effect);
      void this.applyBackground();
      return this.processor.output;
    } catch {
      return cam;
    }
  }

  private async applyDeviceBlur(cam: MediaStreamTrack, on: boolean): Promise<boolean> {
    const available = Boolean((cam.getCapabilities?.() as BlurCapable | undefined)?.backgroundBlur?.includes(true));
    if (available) await cam.applyConstraints({ advanced: [{ backgroundBlur: on } as MediaTrackConstraintSet] }).catch(() => undefined);
    if (this.view) this.update({ deviceBlur: { available, on: available && on } });
    return available;
  }

  private async flushIce() {
    const pc = this.pc;
    if (!pc?.remoteDescription) return;
    for (const c of this.pendingIce.splice(0)) await pc.addIceCandidate(c).catch(() => undefined);
  }

  private busy() {
    return this.view !== null && this.view.phase !== 'ended';
  }

  /** Places a call to the peer of a direct conversation. */
  async start(conversationId: string, peer: CallView['peer'], media: CallMedia): Promise<void> {
    if (this.busy()) return;
    const callId = uuidv7();
    this.view = { callId, conversationId, peer, media, phase: 'outgoing', muted: false, cameraOff: false, connectedAt: null, endReason: null, localStream: null, remoteStream: null, ...this.extras() };
    this.listeners.forEach((l) => l());
    if (!callsSupported()) return this.finish('unsupported');
    let stream: MediaStream;
    try {
      stream = await this.media(media === 'video');
    } catch {
      return this.finish('permission');
    }
    if (this.view?.callId !== callId) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.update({ localStream: stream });
    try {
      const pc = await this.connection();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.rt.command('call.invite', { callId, conversationId, media, sdp: offer.sdp ?? '' }, callId);
    } catch {
      this.finish('failed');
    }
  }

  /** Answers the ringing call; `withVideo` false answers a video call with audio only. */
  async accept(withVideo = true): Promise<void> {
    const view = this.view;
    if (!view || view.phase !== 'incoming' || !this.incomingOffer) return;
    this.stopRinging();
    this.update({ phase: 'connecting' });
    let stream: MediaStream;
    try {
      stream = await this.media(view.media === 'video' && withVideo);
    } catch {
      this.rt.command('call.reject', { callId: view.callId });
      return this.finish('permission');
    }
    this.update({ localStream: stream, cameraOff: view.media === 'video' && !withVideo });
    try {
      const pc = await this.connection();
      await pc.setRemoteDescription({ type: 'offer', sdp: this.incomingOffer });
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.rt.command('call.answer', { callId: view.callId, sdp: answer.sdp ?? '' }, view.callId);
      await this.flushIce();
    } catch {
      this.hangup('failed');
    }
  }

  reject(): void {
    if (!this.view) return;
    this.rt.command('call.reject', { callId: this.view.callId });
    this.finish('rejected');
  }

  hangup(reason: 'hangup' | 'failed' = 'hangup'): void {
    if (!this.view || this.view.phase === 'ended') return;
    this.rt.command('call.hangup', { callId: this.view.callId, reason });
    this.finish(reason === 'failed' ? 'failed' : this.view.phase === 'outgoing' ? 'cancelled' : 'hangup');
  }

  toggleMute(): void {
    const muted = !this.view?.muted;
    this.view?.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
    this.update({ muted });
  }

  toggleCamera(): void {
    const cameraOff = !this.view?.cameraOff;
    this.view?.localStream?.getVideoTracks().forEach((t) => (t.enabled = !cameraOff));
    this.update({ cameraOff });
  }

  private extras(): Pick<CallView, 'effect' | 'deviceBlur' | 'speakerId' | 'background' | 'backgroundStatus'> {
    return {
      effect: this.settings.effect,
      deviceBlur: { available: false, on: false },
      speakerId: this.settings.speakerId,
      background: this.settings.background,
      backgroundStatus: 'idle',
    };
  }

  private needsProcessor(): boolean {
    return this.settings.effect !== 'none' || this.settings.background !== 'none';
  }

  private remember(patch: Partial<CallSettings>): void {
    this.settings = { ...this.settings, ...patch };
    saveCallSettings(this.settings);
  }

  /** Puts a new track into the call without renegotiating (the other side sees/hears the change at once). */
  private async swapTrack(kind: 'audio' | 'video', next: MediaStreamTrack): Promise<void> {
    const view = this.view;
    if (!view?.localStream) return;
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === kind);
    if (sender) await sender.replaceTrack(next);
    const old = kind === 'audio' ? view.localStream.getAudioTracks() : view.localStream.getVideoTracks();
    if (kind === 'audio') next.enabled = !view.muted;
    else next.enabled = !view.cameraOff;
    const stream = new MediaStream([...(kind === 'audio' ? [next] : view.localStream.getAudioTracks()), ...(kind === 'video' ? [next] : view.localStream.getVideoTracks())]);
    // The old camera track may be the effect canvas: its source is handled by the caller.
    for (const t of old) if (t !== next && t !== this.camTrack) t.stop();
    this.update({ localStream: stream });
  }

  async setMicrophone(deviceId: string): Promise<void> {
    this.remember({ micId: deviceId });
    if (!this.view?.localStream) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints() });
    await this.swapTrack('audio', stream.getAudioTracks()[0]!);
  }

  /** A camera by id, or (phones) the front/back camera. */
  async setCamera(choice: { deviceId: string } | { facing: 'user' | 'environment' }): Promise<void> {
    if ('deviceId' in choice) this.remember({ camId: choice.deviceId });
    if (!this.view?.localStream || this.view.media !== 'video') return;
    const old = this.camTrack;
    old?.stop(); // phones often allow only one open camera
    const stream = await navigator.mediaDevices.getUserMedia({ video: this.videoConstraints('facing' in choice ? choice.facing : undefined) });
    const cam = stream.getVideoTracks()[0]!;
    this.camTrack = cam;
    await this.applyDeviceBlur(cam, this.settings.deviceBlur);
    await this.swapTrack('video', this.videoOut(cam));
  }

  setSpeaker(deviceId: string): void {
    this.remember({ speakerId: deviceId });
    this.update({ speakerId: deviceId });
  }

  /** Noise suppression, echo cancellation, automatic gain: applied to the live microphone. */
  async setAudioProcessing(patch: Partial<Pick<CallSettings, 'noiseSuppression' | 'echoCancellation' | 'autoGainControl'>>): Promise<void> {
    this.remember(patch);
    const track = this.view?.localStream?.getAudioTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints(this.audioConstraints());
    } catch {
      // Some browsers only take these when the microphone is opened again.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints() });
      await this.swapTrack('audio', stream.getAudioTracks()[0]!);
    }
  }

  /** Keeps the drawing canvas when one is still needed, otherwise swaps between it and the plain camera. */
  private async refreshVideo(): Promise<void> {
    const cam = this.camTrack;
    if (!cam || !this.view?.localStream) return;
    if (this.processor && this.needsProcessor()) {
      this.processor.setEffect(this.settings.effect);
      await this.applyBackground();
      return;
    }
    await this.swapTrack('video', this.videoOut(cam));
  }

  async setEffect(effect: VideoEffect): Promise<void> {
    this.remember({ effect });
    this.update({ effect });
    await this.refreshVideo();
  }

  async setBackground(background: BackgroundChoice): Promise<void> {
    this.remember({ background });
    this.update({ background });
    await this.refreshVideo();
  }

  /** A picture from the device becomes the background (stored on this device only). */
  async setCustomBackground(file: Blob): Promise<void> {
    this.bitmaps.set('custom', await saveCustomBackground(file));
    await this.setBackground('custom');
  }

  private async backgroundLayer(choice: BackgroundChoice): Promise<BackgroundLayer> {
    if (choice === 'none') return { kind: 'none' };
    if (choice === 'blur' || choice === 'blur-strong') return { kind: 'blur', radius: choice === 'blur' ? 8 : 18 };
    let image = this.bitmaps.get(choice) ?? null;
    if (!image) {
      image = choice === 'custom' ? await loadCustomBackground() : await presetBackground(choice as BackgroundPreset);
      if (image) this.bitmaps.set(choice, image);
    }
    return image ? { kind: 'image', image } : { kind: 'none' };
  }

  /** Loads the person detection on first use, then hands the processor its background. */
  private async applyBackground(): Promise<void> {
    const processor = this.processor;
    const choice = this.settings.background;
    if (!processor) return;
    if (choice === 'none') {
      processor.setBackground({ kind: 'none' }, null);
      if (this.view) this.update({ backgroundStatus: 'idle' });
      return;
    }
    if (this.view) this.update({ backgroundStatus: 'loading' });
    try {
      const [segmenter, layer] = await Promise.all([loadSegmenter(), this.backgroundLayer(choice)]);
      // Something else may have been picked meanwhile.
      if (this.processor !== processor || this.settings.background !== choice) return;
      processor.setBackground(layer, segmenter);
      if (this.view) this.update({ backgroundStatus: 'ready' });
    } catch {
      processor.setBackground({ kind: 'none' }, null);
      if (this.view) this.update({ backgroundStatus: 'failed' });
    }
  }

  async setDeviceBlur(on: boolean): Promise<void> {
    this.remember({ deviceBlur: on });
    if (this.camTrack) await this.applyDeviceBlur(this.camTrack, on);
  }

  /** The camera picture as it comes from the device (for previews of effects). */
  get cameraTrack(): MediaStreamTrack | null {
    return this.camTrack;
  }

  dismiss(): void {
    if (this.view?.phase === 'ended') {
      this.view = null;
      this.listeners.forEach((l) => l());
    }
  }

  private finish(reason: CallFailure) {
    this.stopRinging();
    this.pc?.close();
    this.pc = null;
    this.pendingIce = [];
    this.incomingOffer = null;
    this.view?.localStream?.getTracks().forEach((t) => t.stop());
    this.camTrack?.stop();
    this.camTrack = null;
    this.processor?.stop();
    this.processor = null;
    this.update({ phase: 'ended', endReason: reason, localStream: null, remoteStream: null });
    if (this.clearTimer) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => this.dismiss(), 4000);
  }

  private startRinging() {
    try {
      const ctx = new AudioContext();
      const gain = ctx.createGain();
      gain.gain.value = 0.08;
      gain.connect(ctx.destination);
      const beep = () => {
        const osc = ctx.createOscillator();
        osc.frequency.value = 660;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 0.4);
      };
      beep();
      const timer = setInterval(beep, 1500);
      this.ringer = { stop: () => (clearInterval(timer), void ctx.close()) };
    } catch {
      this.ringer = null; // Autoplay may be blocked until the user interacts with the page.
    }
  }

  private stopRinging() {
    this.ringer?.stop();
    this.ringer = null;
  }

  /** Server events routed here by the chat store. */
  handle(e: ServerEvent): void {
    switch (e.event) {
      case 'call.incoming': {
        // Already in a call on this device: the server marks the newcomer busy on its own only when the
        // user is busy everywhere, so decline it here.
        if (this.busy()) {
          this.rt.command('call.reject', { callId: e.payload.callId });
          return;
        }
        this.incomingOffer = e.payload.sdp;
        this.view = {
          callId: e.payload.callId,
          conversationId: e.payload.conversationId,
          peer: { id: e.payload.caller.id, displayName: e.payload.caller.displayName },
          media: e.payload.media,
          phase: 'incoming',
          muted: false,
          cameraOff: false,
          connectedAt: null,
          endReason: null,
          localStream: null,
          remoteStream: null,
          ...this.extras(),
        };
        this.listeners.forEach((l) => l());
        this.startRinging();
        return;
      }
      case 'call.answered':
        if (this.view?.callId === e.payload.callId && this.view.phase === 'outgoing' && this.pc) {
          this.update({ phase: 'connecting' });
          void this.pc
            .setRemoteDescription({ type: 'answer', sdp: e.payload.sdp })
            .then(() => this.flushIce())
            .catch(() => this.hangup('failed'));
        }
        return;
      case 'call.ice':
        if (this.view?.callId !== e.payload.callId) return;
        if (this.pc?.remoteDescription) void this.pc.addIceCandidate(e.payload.candidate).catch(() => undefined);
        else this.pendingIce.push(e.payload.candidate);
        return;
      case 'call.ended':
        if (this.view?.callId !== e.payload.callId || this.view.phase === 'ended') return;
        // This device answered: the "answered elsewhere" note is for the user's other devices.
        if (e.payload.reason === 'answered_elsewhere' && this.view.phase !== 'incoming') return;
        this.finish(e.payload.reason);
        return;
      case 'error':
        if (this.view && e.payload.reqId === this.view.callId && this.view.phase !== 'ended') {
          this.rt.command('call.hangup', { callId: this.view.callId });
          this.finish(e.payload.code === 'MESSAGING_NOT_ALLOWED' || e.payload.code === 'FORBIDDEN' ? 'not_allowed' : 'failed');
        }
        return;
      default:
        return;
    }
  }
}
